import { Session, SessionOptions } from "@tellescope/sdk";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import {
  useTellescopeInfiniteQuery,
  UseTellescopeInfiniteQueryOptions,
  useTellescopeQuery,
  useTellescopeSessionMutation,
  UseTellescopeSessionMutationOptions,
  useTellescopeSessionQuery,
  UseTellescopeSessionQueryOptions,
} from "../hooks";
import { useQueryClient } from "@tanstack/react-query";
import { TellescopeModelFromName } from "../mappings";
import { throttleFn } from "@tellescope/utils";

export interface ProactiveRefreshOptions {
  enabled?: boolean;
  throttleMs?: number;
  interactionEvents?: Array<"mousemove" | "mousedown" | "keydown" | "touchstart" | "scroll" | "click" | "focus">;
  onProactiveRefreshError?: (error: unknown) => void;
}

export interface UseAuthStateOptions {
  sessionOptions?: SessionOptions;
  isAuthenticatedQueryOptions?: Omit<
    UseTellescopeSessionQueryOptions<"test_authenticated", Error, boolean>,
    "method" | "request" | "session"
  >;
  organizationsQueryOptions?: Omit<
    UseTellescopeInfiniteQueryOptions<
      "organizations",
      "getSome",
      string | undefined,
      Error,
      TellescopeModelFromName<"organizations">[]
    >,
    | "model"
    | "method"
    | "request"
    | "session"
    | "getNextPageParam"
    | "initialPageParam"
    | "paginateRequest"
    | "select"
    | "enabled"
  >;
  proactiveRefresh?: ProactiveRefreshOptions;
  loginMutationOptions?: Omit<UseTellescopeSessionMutationOptions<"authenticate">, "method" | "session">;
  loginWithTokenOptions?: {
    onSuccess?: (user: { requiresMFA?: boolean }) => void;
  };
  logoutMutationOptions?: Omit<UseTellescopeSessionMutationOptions<"logout">, "method" | "session">;
  refreshSessionMutationOptions?: Omit<UseTellescopeSessionMutationOptions<"refresh_session">, "method" | "session">;
}

export const useAuthState = ({
  sessionOptions,
  isAuthenticatedQueryOptions,
  organizationsQueryOptions,
  proactiveRefresh,
  loginMutationOptions,
  loginWithTokenOptions,
  logoutMutationOptions,
  refreshSessionMutationOptions,
}: UseAuthStateOptions = {}) => {
  const queryClient = useQueryClient();

  // Create a wrapper around Session to track mutations
  const sessionStateRef = useRef<{
    session: Session;
    version: number;
    listeners: Set<() => void>;
  }>({
    session: new Session(sessionOptions),
    version: 0,
    listeners: new Set(),
  });

  // Subscribe to session changes
  const subscribe = useCallback((listener: () => void) => {
    sessionStateRef.current.listeners.add(listener);
    return () => {
      sessionStateRef.current.listeners.delete(listener);
    };
  }, []);

  // Get current version
  const getSnapshot = useCallback(() => sessionStateRef.current.version, []);

  // Use sync external store to track session changes
  const sessionVersion = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  // Helper to notify listeners of session changes
  const notifySessionChange = useCallback(() => {
    sessionStateRef.current.version++;
    sessionStateRef.current.listeners.forEach((listener) => listener());
  }, []);

  const session = sessionStateRef.current.session;
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>();
  const requiresMFA = session.userInfo?.requiresMFA ?? false;

  const isAuthenticatedQuery = useTellescopeSessionQuery({
    method: "test_authenticated",
    request: undefined,
    session,
    retry: false,
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000,
    enabled: !requiresMFA,
    ...isAuthenticatedQueryOptions,
  });
  const isAuthenticated = !!session.authToken && !requiresMFA;

  // Return queried user so we don't have to mutate the session in order to have an accurate user after mutations
  const currentUserQuery = useTellescopeQuery({
    model: "users",
    method: "getOne",
    request: session.userInfo.id,
    session,
    enabled: isAuthenticated && !!session.userInfo.id,
  });
  const currentUser = currentUserQuery?.data ?? session.userInfo;

  const organizationsQuery = useTellescopeInfiniteQuery({
    model: "organizations",
    method: "getSome",
    request: {},
    session,
    select: (data) => data.pages.flat(),
    enabled: !!currentUser && isAuthenticated,
    ...organizationsQueryOptions,
  });

  const selectedOrganization = useMemo(
    () =>
      selectedOrganizationId
        ? organizationsQuery.data?.find((org) => org.id === selectedOrganizationId)
        : organizationsQuery.data?.at(-1),
    [organizationsQuery.data, selectedOrganizationId]
  );

  const changeOrganization = useCallback((id?: string) => {
    setSelectedOrganizationId(id);
  }, []);

  const invalidateAuthQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: isAuthenticatedQuery.queryKey, exact: true });
    queryClient.invalidateQueries({ queryKey: organizationsQuery.queryKey, exact: true });
  }, [isAuthenticatedQuery.queryKey, organizationsQuery.queryKey, queryClient]);

  const resetSession = useCallback(
    (options: Partial<SessionOptions> = {}) => {
      sessionStateRef.current.session = new Session({ ...sessionOptions, ...options });
      notifySessionChange();
    },
    [sessionOptions, notifySessionChange]
  );

  const loginMutation = useTellescopeSessionMutation({
    method: "authenticate",
    autoCacheUpdateStrategy: "disabled",
    session,
    ...loginMutationOptions,
    onSuccess: ({ authToken, ...userInfo }, ...rest) => {
      session.setAuthToken(authToken);
      session.setUserInfo(userInfo);
      notifySessionChange();
      if (!userInfo.requiresMFA) {
        invalidateAuthQueries();
      }
      loginMutationOptions?.onSuccess?.({ authToken, ...userInfo }, ...rest);
    },
  });

  const logoutMutation = useTellescopeSessionMutation({
    method: "logout",
    autoCacheUpdateStrategy: "disabled",
    session,
    ...logoutMutationOptions,
    onSuccess: (...args) => {
      queryClient.clear();
      resetSession();
      logoutMutationOptions?.onSuccess?.(...args);
    },
  });

  const loginWithToken = useCallback(
    ({
      authToken,
      user,
    }: {
      authToken: string;
      user: { businessId: string; organizationIds?: string[]; requiresMFA?: boolean };
    }) => {
      session.setAuthToken(authToken);
      session.setUserInfo(user);
      notifySessionChange();
      loginWithTokenOptions?.onSuccess?.(user);
      if (!user.requiresMFA) {
        invalidateAuthQueries();
      }
    },
    [session, notifySessionChange, invalidateAuthQueries, loginWithTokenOptions]
  );

  const logout = useCallback(
    (options?: Parameters<typeof logoutMutation.mutateAsync>[1]) => logoutMutation.mutateAsync(undefined, options),
    [logoutMutation]
  );

  const refreshMutation = useTellescopeSessionMutation({
    method: "refresh_session",
    autoCacheUpdateStrategy: "disabled",
    session,
    ...refreshSessionMutationOptions,
    onSuccess: ({ user, authToken }, ...rest) => {
      session.setAuthToken(authToken);
      session.setUserInfo(user);
      notifySessionChange();
      refreshSessionMutationOptions?.onSuccess?.({ user, authToken }, ...rest);
    },
  });

  // Proactive refresh configuration
  const isRefreshingRef = useRef<boolean>(false);

  const proactiveRefreshConfig = useMemo(() => {
    return {
      enabled: proactiveRefresh?.enabled ?? true,
      throttleMs: proactiveRefresh?.throttleMs ?? 60 * 1000,
      interactionEvents: proactiveRefresh?.interactionEvents ?? [
        "mousemove",
        "mousedown",
        "keydown",
        "touchstart",
        "scroll",
      ],
      onProactiveRefreshError: proactiveRefresh?.onProactiveRefreshError,
    };
  }, [proactiveRefresh]);

  const handleProactiveRefreshUnthrottled = useCallback(async () => {
    if (!proactiveRefreshConfig?.enabled) return;
    if (isRefreshingRef.current) return;
    if (!session.authToken) return;

    isRefreshingRef.current = true;

    try {
      const result = await session.refresh_session_if_expiring_soon();
      if (result) {
        session.setAuthToken(result.authToken);
        session.setUserInfo(result.user);
        notifySessionChange();
      }
    } catch (error) {
      proactiveRefreshConfig.onProactiveRefreshError?.(error);
    } finally {
      isRefreshingRef.current = false;
    }
  }, [proactiveRefreshConfig, session, notifySessionChange]);

  const handleProactiveRefresh = useMemo(
    () => throttleFn(handleProactiveRefreshUnthrottled, proactiveRefreshConfig.throttleMs),
    [handleProactiveRefreshUnthrottled, proactiveRefreshConfig.throttleMs]
  );

  // Set up event listeners for proactive refresh
  useEffect(() => {
    if (!proactiveRefreshConfig?.enabled || !session.authToken) return;

    const events = proactiveRefreshConfig.interactionEvents;
    events.forEach((event) => {
      window.addEventListener(event, handleProactiveRefresh, { passive: true });
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleProactiveRefresh);
      });
    };
  }, [proactiveRefreshConfig, session.authToken, handleProactiveRefresh]);

  const resetAuth = useCallback(() => {
    sessionStateRef.current.session = new Session(sessionOptions);
    notifySessionChange();
  }, [notifySessionChange, sessionOptions]);

  return useMemo(
    () => ({
      session,
      isAuthenticated,
      requiresMFA,
      isLoadingOrganizations: organizationsQuery.isPending || organizationsQuery.isLoading,
      organizations: organizationsQuery.data,
      selectedOrganization,
      changeOrganization,
      organizationError: organizationsQuery.error,
      isInitializing: isAuthenticatedQuery.isLoading || (isAuthenticatedQuery.data && !currentUser),
      currentUser,
      login: loginMutation.mutateAsync,
      loginWithToken,
      loginError: loginMutation.error,
      logout,
      refreshSession: refreshMutation.mutateAsync,
      _sessionVersion: sessionVersion,
      resetAuth,
      resetSession,
    }),
    [
      changeOrganization,
      currentUser,
      isAuthenticated,
      isAuthenticatedQuery.data,
      isAuthenticatedQuery.isLoading,
      loginMutation.error,
      loginMutation.mutateAsync,
      loginWithToken,
      logout,
      organizationsQuery.data,
      organizationsQuery.error,
      organizationsQuery.isLoading,
      organizationsQuery.isPending,
      refreshMutation.mutateAsync,
      requiresMFA,
      resetAuth,
      resetSession,
      selectedOrganization,
      session,
      sessionVersion,
    ]
  );
};
